/* eslint-disable react-hooks/exhaustive-deps */
import React, { createContext, ReactNode, useEffect } from 'react'
import useAuth, { AccountInfo, UpdateAccountInfoParams } from 'hooks/useAuth'
import Cookies from 'js-cookie'
import ChangePasswordDialog from 'components/ChangePasswordDialog'

type AuthContextType = {
  signOut: () => void
  login: (email: string, password: string) => Promise<void>
  fetchAccountInfo: () => Promise<void>
  updateAccountInfo: (params: UpdateAccountInfoParams) => Promise<void>
  isAuthenticated: boolean
  accountInfo: AccountInfo | null
  isLoadingAccountInfo: boolean
  isTermCustomer: boolean
  isImpersonated: boolean
  impersonatedEmail: string | null
}

export const AuthContext = createContext<AuthContextType>({
  signOut: () => {
    throw new Error('signOut method not implemented')
  },
  login: async () => {
    throw new Error('login method not implemented')
  },
  fetchAccountInfo: async () => {
    throw new Error('fetchAccountInfo method not implemented')
  },
  updateAccountInfo: async () => {
    throw new Error('updateAccountInfo method not implemented')
  },
  isAuthenticated: false,
  accountInfo: null,
  isLoadingAccountInfo: false,
  isTermCustomer: false,
  isImpersonated: false,
  impersonatedEmail: null
})

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const {
    signOut,
    login,
    updateAccountInfo,
    fetchAccountInfo,
    isAuthenticated,
    accountInfo,
    isLoadingAccountInfo,
    isImpersonated,
    impersonatedEmail,
    setIsImpersonated,
    setImpersonatedEmail,
    showPasswordChangeDialog,
    setShowPasswordChangeDialog,
    passwordChangeSession,
    passwordChangeUsername
  } = useAuth()

  // Reads the persisted impersonation state once when the provider mounts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const cookieImpersonated = Cookies.get('isImpersonated') === 'true'
    const cookieEmail = Cookies.get('impersonatedEmail') || null
    setIsImpersonated(cookieImpersonated)
    setImpersonatedEmail(cookieEmail)
  }, [])

  // Account data is fetched when authentication changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (isAuthenticated) {
      fetchAccountInfo()
    }
  }, [isAuthenticated])

  const handlePasswordChangeSuccess = () => {
    fetchAccountInfo()
  }

  return (
    <AuthContext.Provider
      value={{
        signOut,
        login,
        fetchAccountInfo,
        updateAccountInfo,
        isAuthenticated,
        accountInfo,
        isLoadingAccountInfo,
        isTermCustomer: !!accountInfo?.isTermCustomer,
        isImpersonated,
        impersonatedEmail
      }}
    >
      {children}
      <ChangePasswordDialog
        open={showPasswordChangeDialog}
        session={passwordChangeSession}
        username={passwordChangeUsername}
        onClose={() => setShowPasswordChangeDialog(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </AuthContext.Provider>
  )
}
